import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';

const API_URL = process.env.REACT_APP_API_URL;

const AuthContext = createContext(null);

// Cookie helper functions
const setCookie = (name, value, days = 7) => {
  const expires = new Date(Date.now() + days * 864e5).toUTCString();
  document.cookie = `${name}=${encodeURIComponent(value)}; expires=${expires}; path=/; SameSite=Lax`;
};

const getCookie = (name) => {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) {
    return decodeURIComponent(parts.pop().split(';').shift());
  }
  return null;
};

const deleteCookie = (name) => {
  document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(getCookie('token'));
  const [loading, setLoading] = useState(true);
  const [sessionStatus, setSessionStatus] = useState({
    currentPlaybackTime: 0,
    maxBufferedTime: 0,
    bufferAhead: 0,
    bufferLimit: 120000, // 2 minutes in ms
    bufferRemaining: 120000,
    limitReached: false
  });

  // Verify token on mount
  useEffect(() => {
    const verifyToken = async () => {
      if (token) {
        try {
          const response = await fetch(`${API_URL}/auth/verify`, {
            headers: {
              'Authorization': `Bearer ${token}`
            },
            credentials: 'include'
          });
          
          if (response.ok) {
            const data = await response.json();
            setUser(data.user);
          } else {
            // Token invalid, clear it
            localStorage.removeItem('token');
            setToken(null);
            setUser(null);
          }
        } catch (error) {
          console.error('Token verification failed:', error);
        }
      }
      setLoading(false);
    };

    verifyToken();
  }, [token]);

  // Fetch session status
  const fetchSessionStatus = useCallback(async (videoId = null) => {
    if (!token) return;
    try {
      const url = videoId 
        ? `${API_URL}/session/status?videoId=${videoId}`
        : `${API_URL}/session/status`;
        
      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` },
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        setSessionStatus({
          currentPlaybackTime: data.currentPlaybackTime,
          maxBufferedTime: data.maxBufferedTime,
          bufferAhead: data.bufferAhead,
          bufferLimit: data.bufferLimit,
          bufferRemaining: data.bufferRemaining,
          limitReached: data.limitReached
        });
      }
    } catch (error) {
      console.error('Failed to fetch session status:', error);
    }
  }, [token]);

  // Update current playback time on server
  const updateWatchTime = useCallback(async (currentTime, videoId = null) => {
    if (!token) return null;
    try {
      const response = await fetch(`${API_URL}/session/update-time`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        credentials: 'include',
        body: JSON.stringify({ currentTime, videoId })
      });
      
      if (response.ok) {
        const data = await response.json();
        setSessionStatus(prev => ({
          ...prev,
          currentPlaybackTime: data.currentPlaybackTime,
          maxBufferedTime: data.maxBufferedTime,
          bufferAhead: data.bufferAhead,
          bufferRemaining: data.bufferRemaining,
          limitReached: data.limitReached
        }));
        return data;
      }
    } catch (error) {
      console.error('Failed to update playback time:', error);
    }
    return null;
  }, [token]);

  // Reset session (after buffer limit reached)
  const resetSession = useCallback(async (videoId = null) => {
    if (!token) return null;
    try {
      const response = await fetch(`${API_URL}/session/reset`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        credentials: 'include',
        body: JSON.stringify({ videoId })
      });
      
      if (response.ok) {
        const data = await response.json();
        setSessionStatus({
          currentPlaybackTime: data.currentPlaybackTime,
          maxBufferedTime: data.maxBufferedTime,
          bufferAhead: data.bufferAhead,
          bufferLimit: data.bufferLimit,
          bufferRemaining: data.bufferRemaining,
          limitReached: data.limitReached
        });
        return data;
      }
    } catch (error) {
      console.error('Failed to reset session:', error);
    }
    return null;
  }, [token]);

  const login = async (identifier, password) => {
    // Determine if identifier is email or username
    const isEmail = identifier.includes('@');
    const body = isEmail 
      ? { email: identifier, password } 
      : { username: identifier, password };

    const response = await fetch(`${API_URL}/auth/login`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      credentials: 'include',
      body: JSON.stringify(body)
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || 'Login failed');
    }

    setCookie('token', data.token);
    setToken(data.token);
    setUser(data.user);
    
    // Reset session status for authenticated user
    setSessionStatus({
      currentPlaybackTime: 0,
      maxBufferedTime: 0,
      bufferAhead: 0,
      bufferLimit: 120000,
      bufferRemaining: 120000,
      limitReached: false
    });

    return data;
  };

  const logout = () => {
    // Call logout endpoint to clear cookie
    fetch(`${API_URL}/auth/logout`, {
      method: 'POST',
      credentials: 'include'
    }).catch(err => console.error('Logout error:', err));
    
    deleteCookie('token');
    setToken(null);
    setUser(null);
    setSessionStatus({
      currentPlaybackTime: 0,
      maxBufferedTime: 0,
      bufferAhead: 0,
      bufferLimit: 120000,
      bufferRemaining: 120000,
      limitReached: false
    });
  };

  const value = {
    user,
    token,
    loading,
    sessionStatus,
    login,
    logout,
    fetchSessionStatus,
    updateWatchTime,
    resetSession,
    isAuthenticated: !!user
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
